import { Fragment, useState } from 'react';
import validator from 'validator';
import { ApiError, ApiValidationError } from '../../utils/api-error';
import BulkTableModal from '../form/bulk-table/BulkTableModal';

type Props = {
  quarter: string;
  missing: string[];
  managerEmail: string;
  onDecision: (send: boolean, managerEmail?: string) => Promise<void>;
  onMissingClose: () => void;
};

export default function ScsQuarterCompletionModal({
  quarter,
  missing,
  managerEmail,
  onDecision,
  onMissingClose,
}: Props) {
  const [choice, setChoice] = useState('');
  const [managerChoice, setManagerChoice] = useState('');
  const [newManagerEmail, setNewManagerEmail] = useState('');
  const [error, setError] = useState('');
  const [managerEmailError, setManagerEmailError] = useState('');
  const [busy, setBusy] = useState(false);
  const currentEmail = managerEmail.trim().toLowerCase();
  const newEmail = newManagerEmail.trim().toLowerCase();
  const emailError =
    managerChoice === 'no' && newEmail
      ? newEmail === currentEmail
        ? 'Your new manager email cannot be the same as your old manager email. If your manager has not changed, select Yes above.'
        : !validator.isEmail(newEmail)
          ? 'Enter a valid email address'
          : managerEmailError
      : '';
  const selectedEmail = managerChoice === 'yes' ? currentEmail : newEmail;
  const canSend = Boolean(managerChoice && validator.isEmail(selectedEmail) && !emailError);
  const finish = async (send: boolean) => {
    if (send && !canSend) return;
    setBusy(true);
    setError('');
    try {
      if (send) await onDecision(true, selectedEmail);
      else await onDecision(false);
    } catch (failure) {
      if (send && managerChoice === 'no' && failure instanceof ApiValidationError) {
        setManagerEmailError(failure.message);
      } else {
        setError(
          failure instanceof ApiError
            ? failure.message
            : 'Unable to save your choice. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  };
  const confirm = () => {
    if (!choice) {
      setError('Select whether to send your form for approval');
      return;
    }
    void finish(choice === 'yes');
  };
  return (
    <BulkTableModal
      title={missing.length ? `Complete your ${quarter} form` : `${quarter} complete`}
      onClose={() => {
        if (!busy) {
          if (missing.length) onMissingClose();
          else void finish(false);
        }
      }}
    >
      {missing.length ? (
        <>
          <p className="govuk-body">
            To complete your {quarter} form, you must complete the below sections:
          </p>
          <ul className="govuk-list govuk-list--bullet">
            {missing.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
          <button type="button" className="govuk-button" onClick={onMissingClose}>
            OK
          </button>
        </>
      ) : (
        <>
          <p className="govuk-body">
            Your Performance form is up to date and ready for approval by your line manager.
          </p>
          {error && (
            <p className="govuk-error-message" role="alert">
              {error}
            </p>
          )}
          <fieldset className="govuk-fieldset" disabled={busy}>
            <legend className="govuk-fieldset__legend">
              Would you like to send your form for approval?
            </legend>
            <div className="govuk-radios">
              {[
                ['yes', 'Yes, send to my manager now'],
                ['no', 'No, I have more changes to make (save as incomplete)'],
              ].map(([value, label]) => (
                <Fragment key={value}>
                  <div className="govuk-radios__item">
                    <input
                      className="govuk-radios__input"
                      type="radio"
                      name="scs-send"
                      id={`scs-send-${value}`}
                      value={value}
                      checked={choice === value}
                      onChange={() => {
                        setChoice(value);
                        setError('');
                      }}
                      aria-controls={value === 'yes' ? 'scs-manager-confirmation' : undefined}
                    />
                    <label
                      className="govuk-label govuk-radios__label"
                      htmlFor={`scs-send-${value}`}
                    >
                      {label}
                    </label>
                  </div>
                  {value === 'yes' && choice === 'yes' && (
                    <div className="govuk-radios__conditional" id="scs-manager-confirmation">
                      <fieldset className="govuk-fieldset" disabled={busy}>
                        <legend className="govuk-fieldset__legend">
                          Is <span style={{ overflowWrap: 'anywhere' }}>{managerEmail}</span> still
                          your line manager?
                        </legend>
                        <div className="govuk-radios govuk-radios--small">
                          {['yes', 'no'].map((value) => (
                            <div className="govuk-radios__item" key={value}>
                              <input
                                className="govuk-radios__input"
                                type="radio"
                                name="scs-current-manager"
                                id={`scs-current-manager-${value}`}
                                value={value}
                                checked={managerChoice === value}
                                onChange={() => {
                                  setManagerChoice(value);
                                  setError('');
                                }}
                                aria-controls={value === 'no' ? 'scs-new-manager' : undefined}
                              />
                              <label
                                className="govuk-label govuk-radios__label"
                                htmlFor={`scs-current-manager-${value}`}
                              >
                                {value === 'yes' ? 'Yes' : 'No'}
                              </label>
                            </div>
                          ))}
                        </div>
                        {managerChoice === 'no' && (
                          <div
                            id="scs-new-manager"
                            className={`govuk-form-group govuk-!-margin-top-4${emailError ? ' govuk-form-group--error' : ''}`}
                          >
                            <label className="govuk-label" htmlFor="scs-new-manager-email">
                              Please enter your new manager's email address
                            </label>
                            <input
                              className={`govuk-input${emailError ? ' govuk-input--error' : ''}`}
                              id="scs-new-manager-email"
                              name="scs-new-manager-email"
                              type="email"
                              autoComplete="off"
                              spellCheck={false}
                              value={newManagerEmail}
                              onChange={(event) => {
                                setNewManagerEmail(event.target.value);
                                setManagerEmailError('');
                                setError('');
                              }}
                              aria-invalid={Boolean(emailError)}
                              aria-describedby={
                                emailError ? 'scs-new-manager-email-error' : undefined
                              }
                            />
                            {emailError && (
                              <p
                                className="govuk-error-message"
                                id="scs-new-manager-email-error"
                                role="alert"
                              >
                                {emailError}
                              </p>
                            )}
                          </div>
                        )}
                      </fieldset>
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </fieldset>
          <div className="govuk-button-group govuk-!-margin-top-4">
            <button
              type="button"
              className="govuk-button"
              disabled={busy || (choice === 'yes' && !canSend)}
              onClick={confirm}
            >
              {busy ? 'Saving...' : choice === 'yes' ? 'Send for approval' : 'OK'}
            </button>
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              disabled={busy}
              onClick={() => void finish(false)}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </BulkTableModal>
  );
}
